import { Injectable, inject, signal, computed } from '@angular/core';
import { Subject } from 'rxjs';
import { MessageService } from 'primeng/api';
import { UtilitiesService } from 'src/app/features/terminated-employees/services/utilities.service';
import { AuthStore } from './auth.store';

@Injectable({ providedIn: 'root' })
export class UtilidadesMontosProgressStore {

  private utilitiesService = inject(UtilitiesService);
  private messageService   = inject(MessageService);
  private authStore        = inject(AuthStore);

  // ── Estado general ──────────────────────────────────────────────────────
  public progress              = signal<number>(0);
  public isActive              = signal<boolean>(false);
  public isDispatching         = signal<boolean>(false);   // fase pre-chunk (zona muerta)
  public isCompleted           = signal<boolean>(false);
  public isPartiallyCompleted  = signal<boolean>(false);
  public isError               = signal<boolean>(false);
  public floatingBarDismissed  = signal<boolean>(false);

  // ── Métricas del polling ───────────────────────────────────────────────
  public velocidad  = signal<number | null>(null);
  public eta        = signal<number | null>(null);
  public procesadas = signal<number>(0);
  public totalFilas = signal<number>(0);
  public validas    = signal<number>(0);
  public conError   = signal<number>(0);
  public errores    = signal<Array<{ fila: number; dni: string; nombre?: string; error: string }>>([]);

  public migrationId = signal<number>(0);
  private _migrationId = 0;
  private timeoutRef: any = null;

  // ── Persistencia (sobrevive refresh de página) ─────────────────────────
  private readonly STORAGE_KEY_PREFIX = 'utilidades_montos_migration_id';
  private get STORAGE_KEY(): string {
    const dni = this.authStore.user()?.dni ?? 'anon';
    return `${this.STORAGE_KEY_PREFIX}_${dni}`;
  }

  // ── Polling adaptativo: rápido al inicio, backoff si no hay avance ─────
  private _currentInterval        = 1_000;   // 1s al inicio (antes 3s — muy lento para archivos pequeños)
  private _lastProgress           = 0;
  private _dispatchPolls          = 0;
  private readonly BASE_INTERVAL  = 1_000;
  private readonly BACKOFF_FACTOR = 1.3;     // suave
  private readonly MAX_INTERVAL   = 10_000;  // máx 10s (antes 30s — demasiado)
  private readonly MAX_DISPATCH_POLLS = 30;  // 30 polls × 1s = 30s máx en dispatch antes de alertar

  private completionSubject = new Subject<void>();
  public completion$ = this.completionSubject.asObservable();

  // ── Mensaje de estado calculado (para el template) ─────────────────────
  public readonly statusMessage = computed(() => {
    if (this.isError()) return 'Error en el procesamiento';

    if (this.isCompleted()) {
      return `Completado: ${this.validas()} registros` +
             (this.conError() > 0 ? `, ${this.conError()} errores` : '');
    }

    if (this.isPartiallyCompleted()) {
      return `Completado con ${this.conError()} errores — ${this.validas()} válidos`;
    }

    if (this.isDispatching()) {
      return 'Enviando a procesamiento...';
    }

    const pct = this.progress();
    if (pct > 0 && pct < 100) {
      const eta = this.eta();
      const etaStr = eta
        ? ` — termina en ${eta < 60 ? Math.ceil(eta) + 's' : Math.ceil(eta / 60) + ' min'}`
        : '';
      return `Procesando: ${this.procesadas()}/${this.totalFilas()}${etaStr}`;
    }

    return 'Procesando...';
  });

  // ── ¿Mostrar barra determinate o indeterminate? ────────────────────────
  public readonly showIndeterminate = computed(() => {
    return this.isActive()
        && this.isDispatching()
        && !this.isCompleted()
        && !this.isPartiallyCompleted()
        && !this.isError();
  });

  // ══════════════════════════════════════════════════════════════════════
  //  API PÚBLICA
  // ══════════════════════════════════════════════════════════════════════

  startTracking(migrationId: number): void {
    localStorage.setItem(this.STORAGE_KEY, String(migrationId));
    this._migrationId = migrationId;
    this.migrationId.set(migrationId);
    this.progress.set(0);
    this.isDispatching.set(true);
    this.isCompleted.set(false);
    this.isPartiallyCompleted.set(false);
    this.isError.set(false);
    this.floatingBarDismissed.set(false);
    this._lastProgress = 0;
    this._dispatchPolls = 0;
    this._currentInterval = this.BASE_INTERVAL;
    this.isActive.set(true);
    this._scheduleNext();
  }

  resumeIfActive(): void {
    if (this.isActive()) return;
    const saved = localStorage.getItem(this.STORAGE_KEY);
    if (!saved) return;
    this._migrationId = Number(saved);
    this.migrationId.set(this._migrationId);
    this._lastProgress = this.progress();
    this._dispatchPolls = 0;
    this._currentInterval = this.BASE_INTERVAL;
    this.isActive.set(true);
    this.isDispatching.set(true);
    this.isCompleted.set(false);
    this.isPartiallyCompleted.set(false);
    this.isError.set(false);
    this.floatingBarDismissed.set(false);
    this._scheduleNext();
  }

  dismiss(): void {
    this.floatingBarDismissed.set(true);
    /* El polling sigue activo — solo oculta la barra flotante */
  }

  acknowledgeComplete(): void {
    this.isCompleted.set(false);
    this.isPartiallyCompleted.set(false);
    this.progress.set(0);
    this.errores.set([]);
  }

  stopTracking(): void {
    this._stopPolling();
    this.progress.set(0);
    this.isDispatching.set(false);
    this.isCompleted.set(false);
    this.isPartiallyCompleted.set(false);
    this.isError.set(false);
    this.errores.set([]);
    localStorage.removeItem(this.STORAGE_KEY);
  }

  // ══════════════════════════════════════════════════════════════════════
  //  POLLING INTERNO
  // ══════════════════════════════════════════════════════════════════════

  private _scheduleNext(): void {
    this.timeoutRef = setTimeout(() => this._poll(), this._currentInterval);
  }

  private _poll(): void {
    this.utilitiesService.getCargaMontosEstado(this._migrationId).subscribe({
      next: (raw: any) => {
        if (!this.isActive()) return // GUARD: si ya terminó, ignorar responses tardíos de polls anteriores
        const data = raw?.data ?? raw;
        const p = data?.progreso;
        const pct = p?.porcentaje ?? 0;
        const procesadas = p?.procesadas ?? 0;

        // ── Detectar transición dispatch → progreso real ──
        if (pct > 0 || procesadas > 0) {
          this.isDispatching.set(false);
          this._dispatchPolls = 0;
        } else if (this.isDispatching()) {
          this._dispatchPolls++;
          if (this._dispatchPolls >= this.MAX_DISPATCH_POLLS) {
            // Lleva 30s sin progreso — alertar pero seguir
            console.warn('[MontosProgress] Sin progreso tras 30s en dispatch');
          }
        }

        // ── Actualizar señales ──
        this.progress.set(pct);
        this.velocidad.set(p?.velocidad_registros_segundo ?? null);
        this.eta.set(p?.tiempo_estimado_restante_segundos ?? null);
        this.procesadas.set(procesadas);
        this.totalFilas.set(p?.total_filas ?? 0);
        this.validas.set(p?.validas ?? 0);
        this.conError.set(p?.con_error ?? 0);

        // ── Polling adaptativo ──
        const delta = pct - this._lastProgress;
        this._currentInterval = delta > 0
          ? this.BASE_INTERVAL              // hay avance → rápido
          : Math.min(this._currentInterval * this.BACKOFF_FACTOR, this.MAX_INTERVAL);
        this._lastProgress = pct;

        // ── Estados terminales ──
        const estado = data?.estado as string;
        if (estado === 'completed') {
          this.errores.set(data?.errores ?? []);
          this._onComplete(false);
        } else if (estado === 'partially_completed') {
          this.errores.set(data?.errores ?? []);
          this._onComplete(true);
        } else if (estado === 'error') {
          this._onError();
        } else {
          this._scheduleNext();
        }
      },
      error: (err: any) => {
        // 404 = migración eliminada → parar silenciosamente
        if (err?.status === 404) {
          this._stopPolling();
          localStorage.removeItem(this.STORAGE_KEY);
          return;
        }
        // Otros errores: reintento con backoff (no matar el polling)
        this._currentInterval = Math.min(
          this._currentInterval * 2, this.MAX_INTERVAL
        );
        this._scheduleNext();
      }
    });
  }

  private _onComplete(partial: boolean): void {
    if (!this.isActive()) return;  
    this._stopPolling();
    this.isDispatching.set(false);
    this.progress.set(100);
    this.isActive.set(false);
    this.isCompleted.set(true);
    this.isPartiallyCompleted.set(partial);
    localStorage.removeItem(this.STORAGE_KEY);
    this.completionSubject.next();

    const summary = partial ? 'Carga con errores parciales' : 'Carga de montos completada';
    const detail = partial
      ? `${this.conError()} registros con error, ${this.validas()} válidos.`
      : `${this.validas()} registros procesados correctamente.`;
    this.messageService.add({ severity: partial ? 'warn' : 'success', summary, detail, life: 6000 });
  }

  private _onError(): void {
    if (!this.isActive()) return; 
    this._stopPolling();
    this.isActive.set(false);
    this.isDispatching.set(false);
    this.isError.set(true);
    localStorage.removeItem(this.STORAGE_KEY);
    this.messageService.add({
      severity: 'error',
      summary: 'Error en carga',
      detail: 'El proceso falló. Consulta el detalle en la tabla de migraciones.',
      life: 6000
    });
  }

  private _stopPolling(): void {
    if (this.timeoutRef) {
      clearTimeout(this.timeoutRef);
      this.timeoutRef = null;
    }
    this.isActive.set(false);
  }
}